import React, { useContext, useEffect, useState } from 'react';
import _ from 'lodash';
import format from 'date-fns/format'

import LegoAdminPageContext from '../../pages/legoAdminPageContext';
import useAsyncEffect from '../common/react-hooks/useAsyncEffect';
import SingleValueSelector from '../common/editors/SingleValueSelector';
import MultiCountryEditor from '../common/editors/MultiCountryEditor';
import downloadFile from '../downloadFile';
import { StateBadge, StateColors } from './StateBadge';

const STATES = ["new", "open", "rejected", "solved", "unclear", "wontfix"];

export function DataIssuesStatistics() {
  const { page } = useContext(LegoAdminPageContext);

  const [stats, setStats] = useState();
  const [groupByField, setGroup] = useState('Day');
  const [countriesFilter, setCountriesFilter] = useState([]);

  const statsService = page.service('/services/data/issues/statistics');

  const downloadCSV = () => {
    if (!stats) return;

    let { solvedByDay, createdByDay } = stats;

    const groupBy = doc => {
      let d = new Date(doc._id.date);
      return {
        Day: doc._id.date,
        Week: format(d, "yyyy 'Week' ww"),
        Month: format(d, 'yyyy-MM (MMM)'),
      }[groupByField]
    }

    let solved = _.groupBy(solvedByDay, groupBy);
    let created = _.groupBy(createdByDay, groupBy);
    let days = _.union(_.keys(solved), _.keys(created)).sort();
    
    // Create CSV content
    const csvRows = ['Date,New,Open,Rejected,Solved,Unclear,Total Created,Solved'];
    days.forEach(date => {
      const stateCounts = {};
      STATES.forEach(state => {
        // Sum all counts for this state across all documents in this time period
        stateCounts[state] = _.sumBy(
          _.filter(created[date], doc => doc._id.state === state),
          'count'
        ) || 0;
      });
      const createdCount = _.sumBy(created[date], 'count') || 0;
      const solvedCount = _.sumBy(solved[date], 'count') || 0;
      csvRows.push(`${date},${stateCounts.new},${stateCounts.open},${stateCounts.rejected},${stateCounts.solved},${stateCounts.unclear},${createdCount},${solvedCount}`);
    });
    
    const csvContent = csvRows.join('\n');
    const fileName = `data-issues-statistics-${groupByField.toLowerCase()}-${format(new Date(), 'yyyy-MM-dd')}.csv`;
    
    downloadFile(csvContent, fileName, 'text/csv;charset=utf-8;');
  };

  useAsyncEffect(async () => {
    const query = {
      daysBefore: groupByField === 'Day' ? 60 : (groupByField === 'Week' ? 365 : 365*5)
    };
    if (countriesFilter.length > 0) {
      query.countriesFilter = countriesFilter;
    }
    setStats(await statsService.find({query}));
  }, [groupByField, countriesFilter]);

  let content;
  if (stats) {
    let { solvedByDay, createdByDay } = stats;

    const groupBy = doc => {
      let d = new Date(doc._id.date);
      return {
        Day: doc._id.date,
        Week: format(d, "yyyy 'Week' ww"),
        Month: format(d, 'yyyy-MM (MMM)'),
      }[groupByField]
    }

    let solved = _.groupBy(solvedByDay, groupBy);
    let created = _.groupBy(createdByDay, groupBy);
    let days = _.union(_.keys(solved), _.keys(created)).sort();
    days.reverse();

    content = <div>
      <div className={'d-flex align-items-center mb-2'}>
          <SingleValueSelector className={'flex-grow-1 ml-2'} value={groupByField} options={['Day', 'Week', 'Month']} onChange={(v) => setGroup(v)}/>
          <MultiCountryEditor className={'flex-grow-1 ml-2'} value={countriesFilter} onChange={(v) => setCountriesFilter(v)} />
        <button className={'btn btn-sm btn-primary ml-2'} onClick={downloadCSV}>
          Download CSV
        </button>
      </div>
      <table className={'table table-sm'}>
        <thead>
        <tr className={'sticky-top bg-white'}>
          <th>Date (last 30 days)</th>
          <th className={'text-right px-3'}>Created</th>
          {STATES.map(state => <th className={'text-right px-2 small'}><StateBadge state={state} /></th>)}
          <th className={'text-right px-4'}>Solved</th>
          <th className={'text-center'}>Solved by model</th>
        </tr>
        </thead>
        <tbody>
        {days.map(date => {
          let maxShown = groupByField === 'Day' ? 20 : 5;

          let groups = _.sortBy(solved[date] || [], 'count').reverse();

          // Extract and aggregate state counts for this time period
          const stateCounts = {};
          STATES.forEach(state => {
            // Sum all counts for this state across all documents in this time period
            stateCounts[state] = _.sumBy(
              _.filter(created[date], doc => doc._id.state === state),
              'count'
            ) || 0;
          });

          const totalCreated = _.sumBy(created[date], 'count');

          return <tr>
            <td className={'text-secondary'}>{date}</td>

            <td className={'text-secondary text-right px-3'}>{totalCreated || '-'}</td>

            {STATES.map(state => <td className={`bg-light-${StateColors[state]} text-right px-2 small`}>
              <div className={'text-dark'}>{stateCounts[state] || '-'}</div>
              <div className={`text-${StateColors[state]} small text-right`}>{((stateCounts[state]||0)*100/totalCreated).toFixed(1)}%</div>
              </td>)}          

            <td className={'text-success text-right px-4'}>{_.sumBy(groups, 'count')}</td>

            <td className={'p-0 bg-light'}>
              <table className={'table table-borderless mb-0 ml-2'}>
                <tbody>
                {_.map(groups.slice(0, maxShown), ({ _id: { date, modelId }, count }) =>
                  <tr className={'small'}>
                    <td width={'70px'} className={'text-success text-right'}>{count}</td>
                    <td>{modelId}</td>
                  </tr>)}

                {groups.length > maxShown ? <tr className={'small'}>
                  <td  width={'70px'} className={'text-success text-right'}>{_.sum(groups.slice(maxShown).map(d => d.count))}</td>
                  <td className={'text-info'}>Other {groups.slice(maxShown).length} models</td>
                </tr> : null}
                </tbody>
              </table>

            </td>

          </tr>;
        })}
        </tbody>
      </table>

      {/*<pre>{JSON.stringify(stats.solvedByDay, true, 2)}</pre>*/}
    </div>;
  } else {
    content = 'Loading statistics...';
  }

  return <div>
    {content}
  </div>;
}
